import { useParams } from "react-router-dom";
import ResourceTable from "@/components/shared/ResourceTable";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import api, { apiError, fmtMoney, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import CashClosure from "@/pages/store/CashClosure";
import CampaignLauncher from "@/pages/store/CampaignLauncher";
import AccountingExport from "@/pages/store/AccountingExport";

const crud = (slug, path) => ({
  fetchList: () => api.get(`/store/${slug}/${path}`).then((r) => r.data),
  createItem: (b) => api.post(`/store/${slug}/${path}`, b),
  updateItem: (id, b) => api.put(`/store/${slug}/${path}/${id}`, b),
  deleteItem: (id) => api.delete(`/store/${slug}/${path}/${id}`),
});

const STATUS = { "À payer": "bg-amber-500/15 text-amber-400", "Payé": "bg-emerald-500/15 text-emerald-400", "Partiellement payé": "bg-blue-500/15 text-blue-400", "Annulé": "bg-red-500/15 text-red-400", "Brouillon": "bg-slate-500/15 text-slate-400", "Envoyée": "bg-emerald-500/15 text-emerald-400", "Programmée": "bg-blue-500/15 text-blue-400" };
const pill = (s) => <span className={`text-xs px-2 py-1 rounded-full ${STATUS[s] || "bg-slate-500/15 text-slate-400"}`}>{s}</span>;

export function Customers() {
  const { slug } = useParams();
  return (
    <div><PageHeader title="Clients" subtitle="Base clients de votre boutique" />
      <ResourceTable testid="customers" title="Clients" createLabel="Nouveau client" {...crud(slug, "customers")}
        columns={[
          { key: "name", label: "Nom" }, { key: "phone", label: "Téléphone" }, { key: "email", label: "Email" },
          { key: "orders_count", label: "Commandes", render: (r) => <span className="mono">{r.orders_count || 0}</span> },
          { key: "total_spent", label: "Total dépensé", render: (r) => <span className="mono">{fmtMoney(r.total_spent)}</span> },
          { key: "last_purchase", label: "Dernier achat", render: (r) => fmtDate(r.last_purchase) },
        ]}
        fields={[
          { name: "name", label: "Nom", type: "text" }, { name: "phone", label: "Téléphone", type: "text" },
          { name: "whatsapp", label: "WhatsApp", type: "text" }, { name: "email", label: "Email", type: "text" },
          { name: "address", label: "Adresse", type: "text" }, { name: "city", label: "Ville", type: "text" },
        ]} />
    </div>
  );
}

export function StoreExpenses() {
  const { slug } = useParams();
  const pay = async (row, reload) => {
    if (!window.confirm("Régler cette charge ? Un décaissement sera créé.")) return;
    try { await api.post(`/store/${slug}/expenses/${row.id}/pay`, {}); toast.success("Charge réglée"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div><PageHeader title="Charges" subtitle="Loyer, salaires, électricité et autres charges" />
      <ResourceTable testid="expenses" title="Charges" createLabel="Nouvelle charge" {...crud(slug, "expenses")}
        rowActions={(row, reload) => row.status !== "Payé" && (
          <Button size="sm" variant="ghost" onClick={() => pay(row, reload)} className="text-emerald-400 hover:text-emerald-300 h-8 mr-1" data-testid="expense-pay-btn">Régler</Button>
        )}
        columns={[
          { key: "label", label: "Libellé" }, { key: "category", label: "Catégorie" },
          { key: "amount", label: "Montant", render: (r) => <span className="mono">{fmtMoney(r.amount)}</span> },
          { key: "supplier", label: "Fournisseur" }, { key: "status", label: "Statut", render: (r) => pill(r.status) },
        ]}
        fields={[
          { name: "label", label: "Libellé", type: "text" },
          { name: "category", label: "Catégorie", type: "select", options: ["Loyer", "Électricité", "Internet", "Salaires", "Transport", "Publicité", "Fournitures", "Maintenance", "Autres"], default: "Autres" },
          { name: "amount", label: "Montant", type: "number" },
          { name: "supplier", label: "Fournisseur", type: "text" },
          { name: "payment_method", label: "Mode de paiement", type: "text" },
          { name: "due_date", label: "Échéance", type: "date" },
          { name: "status", label: "Statut", type: "select", options: ["À payer", "Partiellement payé", "Payé", "Annulé"], default: "À payer" },
          { name: "comment", label: "Commentaire", type: "textarea" },
        ]} />
    </div>
  );
}

export function StoreTreasury() {
  const { slug } = useParams();
  return (
    <div><PageHeader title="Trésorerie" subtitle="Caisses, mobile money, banque et mouvements" />
      <CashClosure slug={slug} />
      <AccountingExport slug={slug} />
      <div className="mb-8">
        <ResourceTable testid="treasury-accounts" title="Caisses & comptes" createLabel="Nouveau compte" {...crud(slug, "treasury-accounts")}
          columns={[{ key: "name", label: "Compte" }, { key: "type", label: "Type" }, { key: "balance", label: "Solde", render: (r) => <span className="mono vp-accent-text">{fmtMoney(r.balance)}</span> }]}
          fields={[{ name: "name", label: "Nom", type: "text" }, { name: "type", label: "Type", type: "select", options: ["cash", "mobile", "bank"], default: "cash" }, { name: "balance", label: "Solde initial", type: "number" }]} />
      </div>
      <ResourceTable testid="treasury-movements" title="Mouvements" createLabel="Nouveau mouvement" {...crud(slug, "treasury-movements")}
        columns={[
          { key: "type", label: "Type", render: (r) => <Badge variant="outline" className={r.type === "in" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}>{r.type === "in" ? "Encaissement" : "Décaissement"}</Badge> },
          { key: "amount", label: "Montant", render: (r) => <span className="mono">{fmtMoney(r.amount)}</span> },
          { key: "reason", label: "Motif" }, { key: "method", label: "Mode" }, { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
        ]}
        fields={[
          { name: "type", label: "Type", type: "select", options: ["in", "out"], default: "in" },
          { name: "amount", label: "Montant", type: "number" }, { name: "reason", label: "Motif", type: "text" },
          { name: "method", label: "Mode de paiement", type: "text", default: "Espèces" },
        ]} />
    </div>
  );
}

export function StoreMarketing() {
  const { slug } = useParams();
  return (
    <div><PageHeader title="Marketing" subtitle="Campagnes email, WhatsApp et SMS vers vos clients" />
      <div className="mb-8">
        <ResourceTable testid="wa-templates" title="Modèles de messages" createLabel="Nouveau modèle" {...crud(slug, "wa-templates")}
          emptyText="Aucun modèle. Créez des messages réutilisables avec {prenom}."
          columns={[
            { key: "name", label: "Nom" },
            { key: "body", label: "Message", render: (r) => <span className="text-slate-400 line-clamp-1 max-w-md inline-block">{r.body}</span> },
          ]}
          fields={[
            { name: "name", label: "Nom du modèle", type: "text" },
            { name: "body", label: "Message (utilisez {prenom} pour le prénom du client)", type: "textarea" },
          ]} />
      </div>
      <ResourceTable testid="store-campaigns" title="Campagnes" createLabel="Nouvelle campagne" {...crud(slug, "campaigns")}
        rowActions={(row, reload) => <CampaignLauncher slug={slug} campaign={row} reload={reload} />}
        columns={[
          { key: "name", label: "Nom" }, { key: "channel", label: "Canal" }, { key: "segment", label: "Segment" },
          { key: "stats", label: "Envois / Clics", render: (r) => <span className="mono">{r.stats?.recipients ?? 0} / {r.stats?.click ?? 0}</span> },
          { key: "status", label: "Statut", render: (r) => pill(r.status) }, { key: "created_at", label: "Créée", render: (r) => fmtDate(r.created_at) },
        ]}
        fields={[
          { name: "name", label: "Nom de la campagne", type: "text" },
          { name: "channel", label: "Canal", type: "select", options: ["Email", "WhatsApp", "SMS"], default: "Email" },
          { name: "segment", label: "Segment", type: "select", options: ["Tous", "Nouveaux clients", "Clients actifs", "Clients inactifs", "Gros acheteurs"], default: "Tous" },
          { name: "subject", label: "Objet", type: "text" },
          { name: "body", label: "Message (tapez {prenom} pour le prénom)", type: "textarea", inserts: () => api.get(`/store/${slug}/wa-templates`).then((r) => r.data.map((t) => ({ label: t.name, value: t.body }))) },
          { name: "status", label: "Statut", type: "select", options: ["Brouillon", "Programmée", "Envoyée"], default: "Brouillon" },
        ]} />
    </div>
  );
}

export function OnlineStore() {
  const { slug } = useParams();
  return (
    <div><PageHeader title="Boutique en ligne" subtitle="Gérez les produits visibles sur votre boutique en ligne">
      <a href={`/store/${slug}`} target="_blank" rel="noreferrer"><Button variant="outline" className="border-white/20 text-white bg-transparent" data-testid="view-storefront-btn">Voir ma boutique ↗</Button></a>
    </PageHeader>
      <ResourceTable testid="online-products" title="Produits en ligne" canCreate={false}
        fetchList={() => api.get(`/store/${slug}/products`).then((r) => r.data)}
        updateItem={(id, b) => api.put(`/store/${slug}/products/${id}`, b)}
        columns={[
          { key: "name", label: "Produit" }, { key: "category", label: "Catégorie" },
          { key: "price", label: "Prix", render: (r) => <span className="mono">{fmtMoney(r.sale_price || r.price)}</span> },
          { key: "online_visible", label: "En ligne", render: (r) => r.online_visible ? <Badge className="bg-emerald-500/15 text-emerald-400">Visible</Badge> : <Badge className="bg-slate-500/15 text-slate-400">Masqué</Badge> },
          { key: "featured", label: "Vedette", render: (r) => r.featured ? "★" : "—" },
        ]}
        fields={[
          { name: "online_visible", label: "Visible en ligne (true/false)", type: "select", options: ["true", "false"] },
          { name: "featured", label: "Produit vedette (true/false)", type: "select", options: ["true", "false"] },
        ]} />
    </div>
  );
}
